import { Dialog, Portal } from '@chakra-ui/react';
import Fuse from 'fuse.js';
import {
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MdSearch } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  MBox,
  MDivider,
  MFlex,
  MIcon,
  MInput,
  MInputElement,
  MSpinner,
} from '../../Monetize';
import MText from '../../Monetize/MText';
import { keepOpenForToasts } from '../../Monetize/Overlays';
import { CommandPaletteItem } from './commandPalette.types';
import { groupResultsByCategory } from './commandPalette.utils';
import { useCommandPaletteItems } from './useCommandPaletteItems';
import { useCommandPaletteRecordItems } from './useCommandPaletteRecordItems';

const FUSE_OPTIONS = {
  keys: ['label', 'description', 'category'],
  threshold: 0.35,
  includeScore: true as const,
};

// Sticky category headers float at the top of the scroll area, so rows reserve
// this much scroll margin (~ the header height) to avoid being scrolled under
// a header when brought into view via keyboard navigation.
const ROW_SCROLL_MARGIN_TOP = 7;

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const CategoryHeader = ({ children }: { children: string }) => (
  <MText
    px={4}
    py={1}
    fontSize="xs"
    fontWeight="semibold"
    color="fg.muted"
    textTransform="uppercase"
    letterSpacing="wide"
    bg="bg.muted"
    position="sticky"
    top={0}
    zIndex={1}
  >
    {children}
  </MText>
);

export const CommandPalette = ({ isOpen, onClose }: CommandPaletteProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeItemRef = useRef<HTMLDivElement>(null);
  const { items, navItems } = useCommandPaletteItems();

  const fuse = useMemo(() => new Fuse(items, FUSE_OPTIONS), [items]);

  const { recordItems, isSearching, isIdQuery, sectionLabel } =
    useCommandPaletteRecordItems({ query, isOpen });

  // Record results never go through Fuse — they arrive pre-filtered from the
  // backend. ID queries put the record section first so Enter lands on it.
  const results: CommandPaletteItem[] = useMemo(() => {
    if (!query) {
      return navItems;
    }
    const fuseResults = fuse.search(query).map(({ item }) => item);
    return isIdQuery
      ? [...recordItems, ...fuseResults]
      : [...fuseResults, ...recordItems];
  }, [fuse, navItems, query, recordItems, isIdQuery]);

  const groupedResults = useMemo(
    () => groupResultsByCategory(results),
    [results],
  );

  // Items in the exact order they render on screen (grouped by category).
  // Keyboard navigation indexes into this so the active item follows the
  // visible list rather than Fuse's score order.
  const orderedResults = useMemo(
    () => Array.from(groupedResults.values()).flat(),
    [groupedResults],
  );

  const flatIndexById = useMemo(
    () => new Map(orderedResults.map((item, index) => [item.id, index])),
    [orderedResults],
  );

  // Show a placeholder record section while the search is debouncing/fetching
  // and hasn't produced any record rows yet.
  const showRecordsLoading = isSearching && recordItems.length === 0;

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex((prev) =>
      Math.min(prev, Math.max(0, orderedResults.length - 1)),
    );
  }, [orderedResults.length]);

  // Keep the active item in view as the selection moves (including wrap-around),
  // so keyboard navigation never lands on a row scrolled out of the viewport.
  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const buildRoute = ({ route: itemRoute, sectionKey }: CommandPaletteItem) => {
    if (!sectionKey) {
      return itemRoute;
    }
    const hashIndex = itemRoute.indexOf('#');
    const base = hashIndex === -1 ? itemRoute : itemRoute.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : itemRoute.slice(hashIndex);
    return `${base}?highlight=${encodeURIComponent(sectionKey)}${hash}`;
  };

  const handleSelect = (item: CommandPaletteItem) => {
    navigate(buildRoute(item));
    onClose();
  };

  const handleSelectInNewTab = (item: CommandPaletteItem) => {
    const win = globalThis.open(
      globalThis.location.origin + buildRoute(item),
      '_blank',
      'noopener,noreferrer',
    );
    if (win) {
      win.opener = null;
    }
    onClose();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (orderedResults.length === 0) {
        return;
      }
      setActiveIndex((prev) => (prev + 1) % orderedResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (orderedResults.length === 0) {
        return;
      }
      setActiveIndex(
        (prev) => (prev - 1 + orderedResults.length) % orderedResults.length,
      );
    } else if (e.key === 'Enter' && orderedResults[activeIndex]) {
      e.preventDefault();
      if (e.metaKey || e.ctrlKey) {
        handleSelectInNewTab(orderedResults[activeIndex]);
      } else {
        handleSelect(orderedResults[activeIndex]);
      }
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
        }
      }}
      motionPreset="none"
      scrollBehavior="inside"
      placement="top"
      size="xl"
      initialFocusEl={() => inputRef.current}
      onInteractOutside={keepOpenForToasts}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            mt="20vh"
            maxH="60vh"
            borderRadius="lg"
            overflow="hidden"
            onKeyDown={handleKeyDown}
          >
            <MBox position="relative">
              <MInputElement
                placement="start"
                pl={4}
                h="full"
                pointerEvents="none"
              >
                <MIcon as={MdSearch} color="fg.muted" />
              </MInputElement>
              <MInput
                ref={inputRef}
                size="lg"
                // v2's `unstyled` Input variant only layered these over the
                // base field style; v3's `unstyled` prop drops the whole recipe.
                bg="transparent"
                h="auto"
                placeholder="Search…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                pl={12}
                pr={4}
                py={5}
                fontSize="lg"
                _placeholder={{ color: 'fg.muted' }}
                borderRadius={0}
              />
            </MBox>

            <MDivider />

            <Dialog.Body p={0} overflowY="auto">
              {results.length === 0 && !showRecordsLoading ? (
                <MFlex justify="center" align="center" py={10}>
                  <MText color="fg.muted" fontSize="sm">
                    No results for &ldquo;{query}&rdquo;
                  </MText>
                </MFlex>
              ) : (
                <>
                  {Array.from(groupedResults.entries()).map(
                    ([category, categoryItems]) => (
                      <MBox key={category}>
                        <CategoryHeader>{category}</CategoryHeader>
                        {categoryItems.map((item) => {
                          const flatIndex = flatIndexById.get(item.id) ?? -1;
                          const isActive = flatIndex === activeIndex;
                          return (
                            <MFlex
                              key={item.id}
                              ref={isActive ? activeItemRef : undefined}
                              scrollMarginTop={ROW_SCROLL_MARGIN_TOP}
                              px={4}
                              py={3}
                              align="center"
                              gap={3}
                              cursor="pointer"
                              bg={isActive ? 'tBlue.hover' : 'transparent'}
                              _hover={{ bg: 'tBlue.hover' }}
                              onClick={(e: MouseEvent) => {
                                if (e.metaKey || e.ctrlKey) {
                                  handleSelectInNewTab(item);
                                } else {
                                  handleSelect(item);
                                }
                              }}
                              onMouseEnter={() => setActiveIndex(flatIndex)}
                              role="option"
                              aria-selected={isActive}
                            >
                              {item.icon && (
                                <MIcon
                                  justifyContent="flex-start"
                                  as={item.icon}
                                  color={isActive ? 'fg.link' : 'fg.subtle'}
                                  w={5}
                                  h={5}
                                  flexShrink={0}
                                />
                              )}
                              <MBox flex={1} minW={0}>
                                <MText
                                  fontSize="md"
                                  fontWeight="medium"
                                  color={isActive ? 'fg.link' : 'fg.primary'}
                                  lineClamp={1}
                                >
                                  {item.label}
                                </MText>
                                {item.description && (
                                  <MText
                                    fontSize="xs"
                                    color="fg.muted"
                                    lineClamp={1}
                                  >
                                    {item.description}
                                  </MText>
                                )}
                              </MBox>
                            </MFlex>
                          );
                        })}
                      </MBox>
                    ),
                  )}
                  {showRecordsLoading && (
                    <MBox>
                      <CategoryHeader>{sectionLabel}</CategoryHeader>
                      <MFlex px={4} py={3} align="center" gap={3}>
                        <MSpinner size="xs" color="fg.muted" />
                        <MText fontSize="sm" color="fg.muted">
                          Searching records…
                        </MText>
                      </MFlex>
                    </MBox>
                  )}
                </>
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
